import { apiClient } from '../../shared/api/apiClient';
import { uploadFile } from '../../shared/api/uploadFile';
import {
    DocsListResponseSchema,
    DocResponseSchema,
    RemoveDocResponseSchema,
    HardDeleteDocsResponseSchema,
    ToggleVisibilityResponseSchema,
    UploadEmbedResponseSchema,
    CreateDocPayloadSchema,
    UpdateDocPayloadSchema,
    DocVersionListResponseSchema,
    DocVersionResponseSchema,
    CreateDocVersionResponseSchema,
    RestoreDocVersionResponseSchema,
} from './schemas';

// DoctexHtml.jsx (el editor rich-text, antes con sus 9 fetch() originales) queda migrado a
// este api.js como parte del refactor que lo saca de ser un god component —
// DocContent.jsx/DocTex.jsx todavía no, quedó fuera de alcance (DocTex.jsx no tiene inserción
// de imágenes; DocContent.jsx no se tocó).
export const docsApi = {
    // La identidad viaja en la cookie de sesión (apiClient manda credentials:'include'),
    // ya no en un header manual.
    list: (params) => {
        let url = '/api/docs';
        if (params?.spaceId) {
            url += `?spaceId=${params.spaceId}`;
        }
        return apiClient.get(url).then((d) => DocsListResponseSchema.parse(d));
    },
    create: (payload) => apiClient.post('/api/docs', CreateDocPayloadSchema.parse(payload)).then((d) => DocResponseSchema.parse(d)),
    getById: (id) => apiClient.get(`/api/docs/${id}`).then((d) => DocResponseSchema.parse(d)),
    update: (id, payload) => apiClient.put(`/api/docs/${id}`, UpdateDocPayloadSchema.parse(payload)).then((d) => DocResponseSchema.parse(d)),
    
    // Sheet endpoints
    getSheetCells: (id) => apiClient.get(`/api/docs/${id}/sheet`),
    updateSheetCells: (id, payload) => apiClient.put(`/api/docs/${id}/sheet`, payload),

    remove: (id) => apiClient.del(`/api/docs/${id}`).then((d) => RemoveDocResponseSchema.parse(d)),
    hardDelete: (payload) => apiClient.post('/api/docs/hard-delete', payload).then((d) => HardDeleteDocsResponseSchema.parse(d)),
    toggleMediaVisibility: (id, mediaKey, access) => apiClient.patch(`/api/docs/${id}/media/${mediaKey}/visibility`, { access }).then((d) => ToggleVisibilityResponseSchema.parse(d)),
    // `cover_img` es un archivo "de una pieza" (no la prosa embebida de arriba, que ya tiene
    // su propio toggle).
    toggleCoverVisibility: (id, access) => apiClient.patch(`/api/docs/${id}/cover/visibility`, { access }).then((d) => ToggleVisibilityResponseSchema.parse(d)),

    notify: (payload) => apiClient.post('/api/notifications/create', payload),
    // Integración de los avances de main (ver .claude/plans/tech-debt): auto-completa el
    // título de un link pegado/creado en LilMenu con el <title> real de la página.
    getLinkTitle: (url) => apiClient.get(`/api/link-title?url=${encodeURIComponent(url)}`),
    createDataIndex: (payload) => apiClient.post('/api/dataindex', payload),
    addDataIndexColumn: (uid, columnName) => apiClient.post(`/api/dataindex/${uid}/columns`, { columnName }),

    // Historial de versiones (integración main→dev, epic D2). `getVersions` = solo metadata;
    // `getVersion` trae el contenido de una versión puntual (para el diff). `createVersion`
    // manda solo `version_name` (el contenido sale del doc vivo server-side).
    getVersions: (id) => apiClient.get(`/api/docs/${id}/versions`).then((d) => DocVersionListResponseSchema.parse(d)),
    getVersion: (id, versionId) => apiClient.get(`/api/docs/${id}/versions/${versionId}`).then((d) => DocVersionResponseSchema.parse(d)),
    createVersion: (id, { versionName } = {}) => apiClient.post(`/api/docs/${id}/versions`, { version_name: versionName }).then((d) => CreateDocVersionResponseSchema.parse(d)),
    restoreVersion: (id, versionId) => apiClient.post(`/api/docs/${id}/versions/${versionId}/restore`).then((d) => RestoreDocVersionResponseSchema.parse(d)),

    // Nuevo tipo `doc_embed` en UPLOAD_TYPE_CONFIG (privado por defecto, con toggle
    // propio) — reemplaza el `type: 'general'` que usaba el editor antes.
    // Subida directa a GCS por
    // debajo (uploadFile ya no pasa por nuestro backend) — se conserva la forma de respuesta
    // {success, url, error} para no tocar los 2 call-sites de useEmbeddedMedia.js.
    uploadEmbed: async (file) => {
        try {
            const url = await uploadFile(file, 'doc_embed');
            return UploadEmbedResponseSchema.parse({ success: true, url });
        } catch (e) {
            return UploadEmbedResponseSchema.parse({ success: false, error: e.message });
        }
    },
};
